import React, { useEffect, useState } from 'react';
import { TextField, Button, Card, CardContent, Typography, Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API from './services/api';

// Main incident report fields (read-only)
const INCIDENT_FIELDS = [
  'TITLE',
  'LOCATION',
  'SEVERITY',
  'DESCRIPTION',
  'DATE',
  'TIME',
  'PRIMARY_DEPARTMENT',
  'EMPLOYEES',
  'CONTRACTORS',
  'FORWARD_TO_WCB',
  'LOST_DAYS',
];

// Map of incident type to its extra fields (read-only)
const INCIDENT_TYPE_FIELDS = {
  'Fatalities': [
    'NAME',         // from FATALITIES_DETAILS
    'CAUSE',        // from FATALITIES_DETAILS
    'DATE',         // from FATALITIES_DETAILS
  ],
  'Injuries Non-lost time': [
    'EMPLOYEE_NAME',      // from INJURIES_NON_LOST_TIME_DETAILS
    'DATE_OF_INJURY',     // from INJURIES_NON_LOST_TIME_DETAILS
    'DESCRIPTION',        // from INJURIES_NON_LOST_TIME_DETAILS
  ],
  'Injuries Lost-time': [
    'EMPLOYEE_NAME',      // from INJURIES_LOST_TIME_DETAILS
    'DATE_OF_INJURY',     // from INJURIES_LOST_TIME_DETAILS
    'HOURS_LOST',         // from INJURIES_LOST_TIME_DETAILS
    'DESCRIPTION',        // from INJURIES_LOST_TIME_DETAILS
  ],
  'Hospital Admittance': [
    'PATIENT_NAME',       // from HOSPITAL_ADMITTANCE_DETAILS
    'DATE_OF_BIRTH',      // from HOSPITAL_ADMITTANCE_DETAILS
    'ADMISSION_DATE',     // from HOSPITAL_ADMITTANCE_DETAILS
    'REASON',             // from HOSPITAL_ADMITTANCE_DETAILS
    'DOCTOR',             // from HOSPITAL_ADMITTANCE_DETAILS
    'NOTES',              // from HOSPITAL_ADMITTANCE_DETAILS
  ],
  'First Aid': [
    'TREATMENT_PROVIDED',     // from FIRST_AID_DETAILS
    'BODY_PART_AFFECTED',     // from FIRST_AID_DETAILS
    'INJURY_TYPE',            // from FIRST_AID_DETAILS
    'MEDICAL_ATTENTION_REQUIRED', // from FIRST_AID_DETAILS
  ],
  // Add more types as needed, using backend field names
};

const toDisplayNameFromEmail = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized.includes('@')) return normalized;

  const localPart = normalized.split('@')[0] || '';
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const parseMultiValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || '').trim()).filter(Boolean);
  }

  const normalized = String(value || '').trim();
  if (!normalized) return [];

  if ((normalized.startsWith('[') && normalized.endsWith(']')) || (normalized.startsWith('{') && normalized.endsWith('}'))) {
    try {
      const parsed = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry || '').trim()).filter(Boolean);
      }
    } catch (error) {
      // Fall back to simple string splitting.
    }
  }

  return normalized
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const formatStaticDisplayValue = (value, fallback = '') => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const formatCollectionDisplay = (value, fallback = 'None recorded') => {
  const entries = parseMultiValue(value)
    .map((entry) => toDisplayNameFromEmail(entry))
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);

  return entries.length > 0 ? Array.from(new Set(entries)).join(', ') : fallback;
};

const normalizeDisplayValue = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;

  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeDisplayValue(entry, ''))
      .filter(Boolean)
      .join(', ') || fallback;
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }

  return String(value).trim() || fallback;
};

const formatYesNoDisplay = (value) => {
  const normalized = normalizeDisplayValue(value, '').toLowerCase();
  if (!normalized) return '';
  if (['true', 'yes', '1', 'y'].includes(normalized)) return 'Yes';
  if (['false', 'no', '0', 'n'].includes(normalized)) return 'No';
  return normalizeDisplayValue(value, '');
};

const formatDateDisplay = (value) => {
  const normalized = normalizeDisplayValue(value, '');
  if (!normalized) return '';

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toLocaleDateString();
};

const formatTimeDisplay = (value) => {
  const normalized = normalizeDisplayValue(value, '');
  if (!normalized) return '';

  const match = normalized.match(/^(\d{2}:\d{2})/);
  if (match) return match[1];
  return normalized;
};

const normalizeDateFieldValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  const directDateMatch = normalized.match(/^\d{4}-\d{2}-\d{2}/);
  if (directDateMatch) return directDateMatch[0];

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '';

  return parsed.toISOString().slice(0, 10);
};

const normalizeDirectoryUser = (user = {}) => ({
  id: user.id || user.ID || user.username || user.USERNAME || user.email || user.EMAIL || '',
  email: String(user.email || user.EMAIL || '').trim(),
  username: String(user.username || user.USERNAME || user.user || user.USER || '').trim(),
  firstName: String(user.firstName || user.first_name || user.FIRST_NAME || '').trim(),
  lastName: String(user.lastName || user.last_name || user.LAST_NAME || '').trim()
});

const formatUppercaseName = (value = '') => String(value || '').trim().toUpperCase();

const buildInvestigationMetadata = (source = {}) => {
  const base = (source && typeof source === 'object') ? source : {};
  const metadata = {
    INVESTIGATOR_NAME: base.INVESTIGATOR_NAME ?? base.investigatorName ?? '',
    ASSIGNEE: base.ASSIGNEE ?? base.assignee ?? '',
    WCB_REPORT_REQUIRED: base.WCB_REPORT_REQUIRED ?? base.wcbReportRequired ?? '',
    WCB_REPORT_REASON: base.WCB_REPORT_REASON ?? base.wcbReportReason ?? '',
    WCB_REPORT_REASON_OTHER: base.WCB_REPORT_REASON_OTHER ?? base.wcbReportReasonOther ?? '',
    OHS_REPORT_REQUIRED: base.OHS_REPORT_REQUIRED ?? base.ohsReportRequired ?? '',
    OHS_REPORT_REASON: base.OHS_REPORT_REASON ?? base.ohsReportReason ?? '',
    OHS_REPORT_REFERENCE: base.OHS_REPORT_REFERENCE ?? base.ohsReportReference ?? ''
  };

  const contributingFactors = base.CONTRIBUTING_FACTORS ?? base.contributingFactors ?? '';
  if (typeof contributingFactors === 'string') {
    const trimmed = contributingFactors.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          metadata.WCB_REPORT_REQUIRED = metadata.WCB_REPORT_REQUIRED || parsed.wcbReportRequired || '';
          metadata.WCB_REPORT_REASON = metadata.WCB_REPORT_REASON || parsed.wcbReportReason || '';
          metadata.WCB_REPORT_REASON_OTHER = metadata.WCB_REPORT_REASON_OTHER || parsed.wcbReportReasonOther || '';
          metadata.OHS_REPORT_REQUIRED = metadata.OHS_REPORT_REQUIRED || parsed.ohsReportRequired || '';
          metadata.OHS_REPORT_REASON = metadata.OHS_REPORT_REASON || parsed.ohsReportReason || '';
          metadata.OHS_REPORT_REFERENCE = metadata.OHS_REPORT_REFERENCE || parsed.ohsReportReference || '';
        }
      } catch (error) {
        // Ignore malformed legacy payloads.
      }
    }
  }

  return metadata;
};

const WCBClerkIncidentReview = ({ incidentId }) => {
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(() => buildInvestigationMetadata());
  const [usersDirectory, setUsersDirectory] = useState([]);
  const [wcbFields, setWcbFields] = useState({
    wcbClaimNumber: '',
    wcbReportSubmittedDate: '',
    wcbNotes: '',
    reviewedIncidentReport: false
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsersDirectory = async () => {
      try {
        const response = await API.get('/api/users');
        const rawUsers = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.users)
            ? response.data.users
            : Array.isArray(response?.data?.usersList)
              ? response.data.usersList
              : [];

        setUsersDirectory(rawUsers.map(normalizeDirectoryUser));
      } catch (directoryError) {
        setUsersDirectory([]);
      }
    };

    const fetchIncident = async () => {
      try {
        setError('');
        const response = await API.get(`/api/incidents/${incidentId}`);
        const incident = response.data.incident
          || (response.data.incidents && response.data.incidents[0])
          || response.data;
        setIncident(incident);
        setInvestigation(buildInvestigationMetadata(incident));
      } catch (err) {
        setError('Failed to fetch incident: ' + (err.response?.data?.error || err.message));
        return;
      }

      try {
        const wcbRes = await API.get(`/api/incidents/${incidentId}/wcb`);
        setWcbFields({
          wcbClaimNumber: wcbRes.data.wcbClaimNumber || '',
          wcbReportSubmittedDate: normalizeDateFieldValue(wcbRes.data.wcbReportSubmittedDate),
          wcbNotes: wcbRes.data.wcbNotes || '',
          reviewedIncidentReport: Boolean(wcbRes.data.reviewedIncidentReport)
        });
      } catch (err) {
        setWcbFields({
          wcbClaimNumber: '',
          wcbReportSubmittedDate: '',
          wcbNotes: '',
          reviewedIncidentReport: false
        });
        setError('Failed to fetch WCB fields: ' + (err.response?.data?.error || err.message));
      }
    };

    if (incidentId) {
      fetchUsersDirectory();
      fetchIncident();
    }
  }, [incidentId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWcbFields({
      ...wcbFields,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await API.patch(`/api/incidents/${incidentId}/wcb`, {
        wcbClaimNumber: String(wcbFields.wcbClaimNumber || '').trim(),
        wcbReportSubmittedDate: normalizeDateFieldValue(wcbFields.wcbReportSubmittedDate),
        wcbNotes: String(wcbFields.wcbNotes || '').trim(),
        reviewedIncidentReport: Boolean(wcbFields.reviewedIncidentReport)
      });
      setSuccess('WCB fields updated successfully.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update WCB fields.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate('/wcb-dashboard', { replace: true });
  };

  const renderTypeFields = () => {
    if (!incident) return null;
    
    // Get incident types as an array
    const incidentTypes = (() => {
      if (Array.isArray(incident.TITLE)) return incident.TITLE;
      if (typeof incident.TITLE === 'string') 
        return incident.TITLE.split(',').map(t => t.trim());
      return [];
    })();
    
    console.log('Detected incident types:', incidentTypes);
    
    // Create array to hold all rendered fields
    const renderedFields = [];
    
    // For each incident type, render its specific fields
    incidentTypes.forEach(type => {
      const fieldKeys = INCIDENT_TYPE_FIELDS[type] || [];
      console.log(`Fields for type ${type}:`, fieldKeys);
      
      fieldKeys.forEach(key => {
        // Try multiple case variations to find the field
        const rawValue = 
          incident[key] || 
          incident[key.toLowerCase()] || 
          incident[key.toUpperCase()] || 
          '';
        const value = normalizeDisplayValue(rawValue, '');
        
        console.log(`Field ${key} value:`, value);
        
        // Only render if there's a value
        if (value !== '' || value === 0) {
          renderedFields.push(
            <Grid item xs={12} sm={6} key={`${type}-${key}`}>
              <TextField
                label={`${type}: ${key.replace(/_/g, ' ')}`}
                value={value}
                fullWidth
                InputProps={{ readOnly: true }}
                variant="outlined"
                margin="dense"
              />
            </Grid>
          );
        }
      });
    });
    
    return renderedFields;
  };

  if (!incident) {
    if (error) return <div>{error}</div>;
    return <div>Loading...</div>;
  }

  if (typeof incident !== 'object') {
    return <div>Unable to display this incident.</div>;
  }

  try {

  const resolveDirectoryPersonName = (...candidates) => {
    const normalizedCandidates = candidates
      .map((candidate) => String(candidate || '').trim().toLowerCase())
      .filter(Boolean);

    if (!normalizedCandidates.length || !usersDirectory.length) {
      return '';
    }

    const match = usersDirectory.find((user) => {
      const email = String(user.email || '').trim().toLowerCase();
      const username = String(user.username || '').trim().toLowerCase();
      return normalizedCandidates.includes(email) || normalizedCandidates.includes(username);
    });

    if (!match) {
      return '';
    }

    const fullName = `${match.firstName || ''} ${match.lastName || ''}`.trim();
    return formatUppercaseName(fullName);
  };

  const resolveBestPersonDisplay = ({ name = '', email = '', username = '' } = {}) => {
    const normalizedName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const normalizedUsername = String(username || '').trim();

    const directoryResolvedName = resolveDirectoryPersonName(
      normalizedEmail,
      normalizedUsername,
      normalizedName
    );

    if (directoryResolvedName) {
      return directoryResolvedName;
    }

    if (normalizedName) {
      return formatUppercaseName(normalizedName);
    }

    if (normalizedEmail) {
      return formatUppercaseName(toDisplayNameFromEmail(normalizedEmail));
    }

    if (normalizedUsername) {
      return formatUppercaseName(normalizedUsername);
    }

    return 'N/A';
  };

  const resolveIncidentSubjectDisplay = (srcIncident = {}) => {
    const employeeCollectionDisplay = formatCollectionDisplay(
      srcIncident.EMPLOYEES
      || srcIncident.employees
      || srcIncident.EMPLOYEE_ID
      || srcIncident.employee_id
      || srcIncident.employeeId,
      ''
    );

    const subjectNameValue = String(
      srcIncident.EMPLOYEE_NAME
      || srcIncident.employeeName
      || srcIncident.REPORTING_EMPLOYEE_NAME
      || srcIncident.reportingEmployeeName
      || ''
    ).trim();

    const subjectEmailValue = String(
      srcIncident.EMPLOYEE_ID
      || srcIncident.employee_id
      || srcIncident.employeeId
      || srcIncident.EMPLOYEES
      || srcIncident.employees
      || srcIncident.employeeEmail
      || srcIncident.USER
      || srcIncident.user
      || ''
    ).trim();

    if (employeeCollectionDisplay) {
      return employeeCollectionDisplay;
    }

    return resolveBestPersonDisplay({
      name: subjectNameValue,
      email: subjectEmailValue,
      username: subjectEmailValue
    });
  };

  const employeeSubjectDisplay = resolveIncidentSubjectDisplay(incident);

  const contractorDisplay = formatCollectionDisplay(
    incident.CONTRACTORS || incident.contractors,
    'None recorded'
  );

  const lostDaysDisplay = formatStaticDisplayValue(
    incident.LOST_DAYS ?? incident.lostDays ?? incident.lost_days,
    '0'
  );

  const isFireDepartmentIncident = String(
    incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || ''
  ).toLowerCase().includes('fire');

  const investigationPath = `${isFireDepartmentIncident ? '/fire-incident-investigations' : '/incident-investigations'}?incidentId=${encodeURIComponent(String(incident.ID || incident.id || incidentId || ''))}&wcbReadonly=1`;

  const handleOpenOriginalInvestigation = () => {
    navigate(investigationPath);
  };

  const wcbReportRequired = normalizeDisplayValue(investigation?.WCB_REPORT_REQUIRED ?? investigation?.wcbReportRequired ?? '', '');
  const wcbReportReason = normalizeDisplayValue(investigation?.WCB_REPORT_REASON ?? investigation?.wcbReportReason ?? '', '');
  const wcbReportReasonOther = normalizeDisplayValue(investigation?.WCB_REPORT_REASON_OTHER ?? investigation?.wcbReportReasonOther ?? '', '');
  const ohsReportRequired = normalizeDisplayValue(investigation?.OHS_REPORT_REQUIRED ?? investigation?.ohsReportRequired ?? '', '');
  const ohsReportReason = normalizeDisplayValue(investigation?.OHS_REPORT_REASON ?? investigation?.ohsReportReason ?? '', '');
  const ohsReportReference = normalizeDisplayValue(investigation?.OHS_REPORT_REFERENCE ?? investigation?.ohsReportReference ?? '', '');

  return (
    <Card sx={{ maxWidth: 800, margin: '0 auto', mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>Incident Review</Typography>
        <Grid container spacing={2}>
          {/* Render only whitelisted incident fields */}
          {INCIDENT_FIELDS.map((key) => {
            let value = normalizeDisplayValue(incident[key] ?? incident[key.toLowerCase()], '');
            if (key === 'EMPLOYEES') value = employeeSubjectDisplay;
            if (key === 'CONTRACTORS') value = contractorDisplay;
            if (key === 'LOST_DAYS') value = lostDaysDisplay;
            if (key === 'FORWARD_TO_WCB') value = formatYesNoDisplay(value);
            if (key === 'DATE' && value) value = formatDateDisplay(value);
            if (key === 'TIME' && value) value = formatTimeDisplay(value);

            const label = {
              EMPLOYEES: 'Employee',
              CONTRACTORS: 'Contractors',
              LOST_DAYS: 'Days Lost'
            }[key] || key.replace(/_/g, ' ');

            return (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  label={label}
                  value={value}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
            );
          })}
          {/* Show incident types */}
          <Grid item xs={12}>
            <TextField
              label="Incident Type(s)"
              value={incident ? normalizeDisplayValue(incident.TITLE, '') : ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
              margin="dense"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenOriginalInvestigation}
              sx={{ mt: 1 }}
            >
              Open Original Incident Investigation
            </Button>
          </Grid>
          {/* Render type-specific fields */}
          {renderTypeFields()}

          {/* Append investigation WCB/OHS reporting question answers */}
          {(wcbReportRequired || wcbReportReason || wcbReportReasonOther || ohsReportRequired || ohsReportReason || ohsReportReference) && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2 }}>WCB / OHS Reporting (Investigation Notes - Read Only)</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WCB Report Required"
                  value={formatYesNoDisplay(wcbReportRequired)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WCB Report Reason"
                  value={wcbReportReason}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
              {String(wcbReportReason || '').toLowerCase() === 'other' && (
                <Grid item xs={12}>
                  <TextField
                    label="WCB Report Reason (Other)"
                    value={wcbReportReasonOther}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    margin="dense"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="OHS Report Required"
                  value={formatYesNoDisplay(ohsReportRequired)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="OHS Report Reason"
                  value={ohsReportReason}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
              {(ohsReportRequired || ohsReportReason) && (
                <Grid item xs={12}>
                  <TextField
                    label="OHS Report Reference"
                    value={ohsReportReference}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    margin="dense"
                  />
                </Grid>
              )}
            </>
          )}

          {/* Editable WCB fields */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>WCB / Insurance Information to Update:</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="WCB Claim Number"
              name="wcbClaimNumber"
              value={wcbFields.wcbClaimNumber}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="WCB Report Submitted Date"
              name="wcbReportSubmittedDate"
              type="date"
              value={wcbFields.wcbReportSubmittedDate}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="WCB Notes"
              name="wcbNotes"
              value={wcbFields.wcbNotes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              margin="dense"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  name="reviewedIncidentReport"
                  checked={Boolean(wcbFields.reviewedIncidentReport)}
                  onChange={handleChange}
                />
              )}
              label="Reviewed Incident Report, by clicking this box no more notifications to the CAO will be sent."
            />
          </Grid>
        </Grid>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', marginTop: '16px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save WCB Report'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={saving}
          >
            Close
          </Button>
        </div>
        {success && <Typography color="success.main" sx={{ mt: 2 }}>{success}</Typography>}
        {error && <Typography color="error.main" sx={{ mt: 2 }}>{error}</Typography>}
      </CardContent>
    </Card>
  );
  } catch (renderError) {
    console.error('Failed to render WCB clerk incident review:', renderError, {
      incidentId,
      incident,
      investigation,
    });

    return (
      <Card sx={{ maxWidth: 800, margin: '0 auto', mt: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Incident Review</Typography>
          <Typography color="error.main">
            This incident could not be displayed. {renderError?.message || 'Unexpected render error.'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </CardContent>
      </Card>
    );
  }
};

export default WCBClerkIncidentReview;